import { Route } from 'vue-router';

import { DocumentValueCodes } from '@/enums/documentValueCodes.enum';
import { EntityType } from '@/enums/entityType.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Storage } from '@/enums/storage.enum';
import store from '@/store/store';
import { flattenNestedQueries } from './document';
import { isNullable } from './isNullable';
import { tryParseInt } from './number';
import { parseJsonOrDefault } from './parseJsonOrDefault';
import { prepareQuery } from './prepareQuery';
import { getSelectedReportType } from './reportTypes';

/**
 * Pobiera statystyki
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getStatistics(route: Route): Promise<any> {
  return store.dispatch('statistics/getProcedureStatistics', {
    procedureId: tryParseInt(route.params.procedureId),
    filters: { filterFields: getReportFilters(route) }
  });
}

/**
 * Pobiera raport finansowy
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getFinancialReport(route: Route): Promise<any> {
  return Promise.all([
    store.dispatch('documentIndex/getFinancialReport', getFinancialReportParams(route)),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.FINANCIAL_REPORT_AVAILABLE_FILTERS,
      entityId: route.params.procedureId.toString(),
      entityType: EntityType.PROCEDURE
    })
  ]);
}

/**
 *  Zwraca parametry raportu finansowego
 *
 * @export
 * @param {Route} route
 * @returns {*}
 */
export function getFinancialReportParams(route: Route): any {
  const filters = getStorageFilters(Storage.REPORT_FINANCIAL_FILTERS, route.params.procedureId.toString());
  return {
    procedureId: tryParseInt(route.params.procedureId),
    structureIds: store.state.auth.nodes?.map((node: any) => node.id),
    filters: {
      filterFields: filters?.length ? { $and: filters } : undefined
    }
  };
}

/**
 * Pobiera ewidencję
 *
 * @export
 * @param {Route} route
 * @returns
 */
export function getReportList(route: Route) {
  const filterFields = getReportFilters(route);
  const unwinds = getReportUnwinds(route);

  return store.dispatch('documentIndex/getStatistics', {
    filters: { ...prepareQuery(route), count: -1, filterFields },
    joinChildren: !!store.state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value,
    unwinds
  });
}

/**
 * Pobiera raporty
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export async function getReportGroups(route: Route): Promise<any> {
  const entityType = EntityType.PROCEDURE;
  const entityId = route.params.procedureId.toString();
  await Promise.all([
    store.dispatch('setting/findPublic', { code: PublicSettings.HARDCODED_REPORTS, entityType, entityId }),
    store.dispatch('setting/findPublic', { code: PublicSettings.HIDE_GENERAL_REPORT, entityType, entityId })
  ]);

  return store.dispatch('documentIndex/getStatistics', getReportGroupsParams(route));
}

/**
 * Zwraca parametry do pobrania raportu
 *
 * @export
 * @param {Route} route
 * @returns {*}
 */
export function getReportGroupsParams(route: Route): any {
  const selectedReportType = getSelectedReportType(route);

  const filterFields = { ...selectedReportType?.staticFilters, ...getReportFilters(route) };
  const order = route?.query?.order as string;

  const procedureId = route.params.procedureId.toString();

  const storageGroups = sessionStorage.getItem(Storage.REPORT_GROUPS + procedureId);
  const selectedGroups: any[] = parseJsonOrDefault(storageGroups, []);
  const staticGroups = (selectedReportType?.staticGroups as any[]) || [];

  const nameFields = selectedGroups.filter(column => !column.rule).map(column => column.name);
  const sortByFields = selectedGroups.filter(column => !!column.sortBy).map(column => column.sortBy);
  const staticFields = staticGroups.filter(group => !group.rule).map(group => group.name);

  const selectedComplexGroups = selectedGroups.filter(column => !!column.rule);
  const staticComplexGroups = staticGroups.filter(group => !!group.rule);

  return {
    complexGroups: [...selectedComplexGroups, ...staticComplexGroups].map(group => ({
      name: group.name,
      rule: group.rule,
      ruleName: group.ruleName
    })),
    groups: [...nameFields, ...sortByFields, ...staticFields],
    filters: { filterFields },
    sort: order
      ? {
          order: order === 'count' ? order : '_id.' + order.replace(/\./g, '__'),
          desc: route.query.desc
        }
      : { order: 'count', desc: false },
    unwinds: selectedReportType?.unwinds
  };
}

/**
 * Zwraca listę wybranych filtrów
 *
 * @export
 * @param {Route} route
 * @param {*} [reportTypeFilters]
 * @returns {*}
 */
export function getReportFilters(route: Route, reportTypeFilters?: any): any {
  const procedureId = route.params.procedureId;

  return {
    procedureId: tryParseInt(procedureId),
    $and: [
      { [`values.${DocumentValueCodes.DELIVERY_DATE}`]: { $exists: true } },
      ...(reportTypeFilters || getStorageFilters(Storage.REPORT_FILTERS, procedureId.toString()))
    ]
  };
}

/**
 * Zwraca filtry zapisane w storage
 *
 * @param {string} storageKey
 * @param {string} procedureId
 * @returns {any[]}
 */
function getStorageFilters(storageKey: string, procedureId: string): any[] {
  const storageFiltersJSON = sessionStorage.getItem(storageKey + procedureId);
  const storageFilters: any[] = parseJsonOrDefault(storageFiltersJSON, []);

  const filters = storageFilters
    .filter(filter => filter.value && filter.value !== 'null')
    .map(filter => {
      if (Array.isArray(filter.value)) {
        const values = filter.value.map((value: any) =>
          filter.isJson ? { [filter.name]: parseJsonOrDefault(value) } : { [filter.name]: value }
        );
        return { $or: values };
      } else {
        return { [filter.name]: filter.isJson ? parseJsonOrDefault(filter.value) : filter.value };
      }
    });

  filters.forEach(filter => flattenNestedQueries(filter));

  return filters.filter(filter => filter && Object.values(filter).filter(value => !isNullable(value)).length);
}

/**
 * Zwraca listę pól po których ma nastąpić rozwinięcie tablicy wyników
 *
 * @export
 * @param {Route} route
 * @returns {string[]}
 */
export function getReportUnwinds(route: Route): string[] {
  const procedureId = route.params.procedureId.toString();
  const storageColumnsJSON = sessionStorage.getItem(Storage.REPORT_COLUMNS + procedureId);
  const storageColumns: any[] = parseJsonOrDefault<any[]>(storageColumnsJSON, []);

  if (!storageColumns.length) {
    return [];
  }

  const allUnwinds = storageColumns
    .map(column => column.unwind)
    .filter(Boolean)
    .reduce<string[]>((accumulator, currentUnwinds) => accumulator.concat(currentUnwinds), []);
  return allUnwinds.filter((unwind, index) => allUnwinds.indexOf(unwind) === index).sort((a, b) => a.length - b.length);
}
